import {Event, EventRegistrationStatus} from '@/apiTypes/event';
import {useAuth} from '@/components/AuthProvider';
import {Button} from '@/components/ui/button';
import {Typography} from '@/components/ui/typography';
import {useAnalytics} from '@/hooks/useAnalytics';
import {
  useEventRegistrationActor,
  useEventRegistrationState,
} from '@/machines/eventRegistrationMachine/eventRegistrationContext';
import {
  getRegistrationPlatform,
  getSourceAttribution,
} from '@/utils/eventDisplayUtils';
import {FC, ReactNode} from 'react';
import {TrackedConfirmation} from '../trackedConfirmation/trackedConfirmation';
import {ActionRow} from './actionRow';
import {NativeRegistrationPanel} from './nativeRegistrationPanel';

interface RegistrationPanelProps {
  event: Event;
  isTracking: boolean;
  isAdmin: boolean;
  isMember: boolean;
  registrationStatus: EventRegistrationStatus;
  /** Rendered beside the primary action button (e.g. a Share button). */
  actionAccessory?: ReactNode;
}

/**
 * Top-level registration panel dispatch for the event page. Routes to:
 * - Indexed-event Track flow when `event.sourceUrl` is set (Track & Register
 *   button or Tracked confirmation). The post-track handoff modal is rendered
 *   by `<EventRegistrationModals>` at the route level alongside the other
 *   machine-driven modals.
 * - The "Your Event" admin button when the viewer owns the event.
 * - Native registration via `eventRegistrationMachine` when an actor is
 *   present.
 * - `null` for ineligible native cases the legacy sidebar still owns.
 */
export const RegistrationPanel: FC<RegistrationPanelProps> = ({
  event,
  isTracking,
  isAdmin,
  isMember,
  registrationStatus,
  actionAccessory,
}) => {
  const registrationActor = useEventRegistrationActor();
  const state = useEventRegistrationState();
  const {profile} = useAuth();
  const {track} = useAnalytics();

  if (event.sourceUrl) {
    if (isTracking) {
      return (
        <div className="w-full flex flex-col gap-3">
          <TrackedConfirmation
            event={{
              id: event.id,
              sourceUrl: event.sourceUrl,
              externalId: event.externalId,
              eventType: event.eventType,
            }}
            actionAccessory={actionAccessory}
          />
        </div>
      );
    }

    const platform =
      getRegistrationPlatform(event.externalId) ??
      getSourceAttribution(event.sourceUrl)?.name ??
      null;
    const isBusy =
      state.matches({flow: 'submitting'}) || state.matches({flow: 'needsAuth'});

    const handleTrack = () => {
      track('Registration Button Clicked', {
        event_id: event.id,
        event_name: event.name,
        intent: 'track',
        platform,
        authenticated: profile != null,
      });
      registrationActor?.send({type: 'TRACK'});
    };

    return (
      <div className="w-full flex flex-col gap-3">
        <ActionRow accessory={actionAccessory}>
          <Button className="w-full" disabled={isBusy} onClick={handleTrack}>
            Track & Register
          </Button>
        </ActionRow>
      </div>
    );
  }

  if (isAdmin) {
    return (
      <ActionRow accessory={actionAccessory}>
        <Button disabled className="w-full" variant="outline">
          Your Event
        </Button>
      </ActionRow>
    );
  }

  if (event.requiresClubMembership && !isMember) {
    const organizerName = event.organizer?.name;
    const membersOnlyNote = (
      <Typography level="B4" className="text-warning-700 text-center">
        {organizerName
          ? `Must be a ${organizerName} member to join.`
          : 'Membership required to join.'}
      </Typography>
    );

    if (!profile) {
      return (
        <div className="flex flex-col gap-2">
          {registrationActor ? (
            <NativeRegistrationPanel
              registrationStatus={registrationStatus}
              actionAccessory={actionAccessory}
            />
          ) : null}
          {membersOnlyNote}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <ActionRow accessory={actionAccessory}>
          <Button
            disabled
            size="lg"
            className="w-full border border-warning-300 text-warning-800 font-medium bg-warning-50 hover:bg-warning-50"
          >
            Members only
          </Button>
        </ActionRow>
        {membersOnlyNote}
      </div>
    );
  }

  if (registrationActor) {
    return (
      <NativeRegistrationPanel
        registrationStatus={registrationStatus}
        actionAccessory={actionAccessory}
      />
    );
  }

  return null;
};
